import React, { Fragment } from "react";
import {
  Link,
  DataTable as ShopifyDataTable,
  Button,
  Checkbox,
  Badge,
} from "@shopify/polaris";
import Currency from "components/format/currency/currency";
import { useTranslation } from "react-i18next";
import PrettyDate from "components/format/PrettyDate";
import { useNarrowScreen } from "./useNarrowScreen";
import _ from "lodash";
import { Column } from "types/Datatable";
import { hasPdfExtension } from "utils/helperFunctions";

type Props = {
  collection: any[];
  columns: Column[];
  isTotalsDisabled?: boolean;
  getRowStyle?: (item: any, index: number) => React.CSSProperties;
};

const DataTable: React.FC<Props> = ({
  collection,
  columns,
  isTotalsDisabled = false,
  getRowStyle,
}: Props) => {
  const { t } = useTranslation();
  const isNarrowScreen = useNarrowScreen();

  const totals: any[] = columns.map((column: Column) => {
    if (column.type === "currency" || column.type === "number") {
      return 0;
    }
    return "";
  });

  const renderClickableColumn = (column: Column, item: any, value: any) => {

    return column.type !== "specificAction" ? (
      <div
        style={{
          cursor: "pointer",
          // height: "35px",
          display: "flex",
          alignItems: "center",
        }}
        onClick={() =>
          column.onClick?.(item)
        }
      >
        {column.type === "clickabledate" ? (
          value ? (
            <PrettyDate
              dateString={value || ""}
              showTime={!!column.isShowTime}
            />
          ) : (
            <span>-</span>
          )
        ) : column.type === "downloadurl" || column.type === "actionBtn" ? (<Button
          target={"_blank"}
          disabled={column.disabled && typeof column.disabled === 'function' ? column.disabled(item) : column.disabled}
          onClick={() =>
            column.onClick?.(item)
          }
        >{typeof column.content === 'function' ? column.content(item) : column.content}</Button>
        ) : (
          <>{value || "-"}</>
        )}
      </div>
    ) : (
      <div
        style={{
          // height: "35px",
          display: "flex",
          alignItems: "center",
        }}
        onClick={() => column.onClick?.(item)}
      >
        <Button
          icon={column.Icon}
        ></Button>
      </div>
    );
  };

  const renderColumnValue = (
    column: Column,
    item: any
  ): JSX.Element | string => {
    let value = _.get(item, column.key, "-");
    value = column.translationRequired ? t(value) : value;

    switch (column.type) {
      case "badge":
        return (<Badge tone={item.tone}>{value}</Badge>);
      case "html_node":
        return <div onClick={() => column.onClick?.(item)} dangerouslySetInnerHTML={{ __html: value }} />;
      case "react_node":
        return value;
      case "currency":
        return <Currency value={value} isSymbol={true} isDecimal={true} />;
      case "date":
        return <PrettyDate dateString={value} />;
      case "datetime":
        return <PrettyDate dateString={value} showTime={true} />;
      case "downloadurl":
      case "actionBtn":
      case "clickableColumn":
      case "clickabledate":
      case "specificAction":
      case "toggleValue":
        return renderClickableColumn(column, item, value);
      case "checkbox":
        return (
          <Checkbox
            label="access"
            labelHidden
            checked={value || false}
            onChange={() => column.onClick?.(item)}
          />
        );
      default:
        if (column.url) {
          return (
            <Link
              removeUnderline
              url={_.get(item, column.url)}
              key={column.url}
            >
              {value}
            </Link>
          );
        } else if (column.onClick) {
          return (
            <div
              style={{ cursor: "pointer" }}
              onClick={() => column.onClick?.(item)}
            >
              <strong>{value}</strong>
            </div>
          );
        }
        const suffixValue = column.suffix ? _.get(item, column.suffix) : "";
        return `${value}${suffixValue ? ` ${suffixValue}` : ""}`;
    }
  };

  const tableData = collection.map((item: any, index: number) =>
    columns.map((column: Column, columnIndex: number) => {
      let value = _.get(item, column.key, "-");
      value = column.translationRequired ? t(value) : value;

      if (
        column.isDisabledTotal !== true &&
        (column.type === "currency" || column.type === "number")
      ) {
        if (!column.totalType || column.totalType === "sum") {
          totals[columnIndex] += value;
        }
      }

      if (column.key === "#") {
        return index + 1;
      }

      return renderColumnValue(column, item);
    })
  );

  const columnContentTypes: ("text" | "numeric")[] = columns.map(
    (column: Column) => {
      if (column.type === "currency" || column.type === "number") {
        return "numeric";
      } else {
        return "text";
      }
    }
  );

  const headings = columns.map((column: Column) => column.name || "");
  const totalsFormatted: any[] | undefined = !isTotalsDisabled
    ? columns.map((column: Column, index: number) => {
      if (column.isDisabledTotal === true) return "";
      if (column.type === "currency") {
        return (
          <Currency value={totals[index]} isSymbol={true} isDecimal={true} />
        );
      }
      if (column.type === "number") {
        return <Currency value={totals[index]} />;
      }
      if (column.key === "#") {
        return collection.length;
      }
      return "";
    })
    : undefined;

  if (isNarrowScreen) {
    return (
      <Fragment>
        {collection.map((item: any, index: number) => {
          const topLeftColumns = columns.filter(
            (col: Column) => col.mobileView === "topLeft"
          );
          const topRightColumns = columns.filter(
            (col: Column) => col.mobileView === "topRight"
          );
          const bottomLeftColumns = columns.filter(
            (col: Column) => col.mobileView === "bottomLeft"
          );
          const bottomRightColumns = columns.filter(
            (col: Column) => col.mobileView === "bottomRight"
          );

          const rowStyle = getRowStyle ? getRowStyle(item, index) : {};

          // Helper function to render multiple columns in the same position
          const renderMultipleColumns = (columns: Column[], isRightAligned = false) => {
            if (columns.length === 0) return null;
            if (columns.length === 1) {
              return renderColumnValue(columns[0], item);
            }
            
            // For multiple columns, stack them vertically with proper spacing
            return (
              <div style={{ 
                display: "flex", 
                flexDirection: "column", 
                gap: "4px",
                alignItems: isRightAligned ? "flex-end" : "flex-start"
              }}>
                {columns.map((column, colIndex) => (
                  <div key={colIndex} style={{ 
                    lineHeight: "1.2",
                    textAlign: isRightAligned ? "right" : "left"
                  }}>
                    {renderColumnValue(column, item)}
                  </div>
                ))}
              </div>
            );
          };

          return (
            <div
              key={index}
              style={{
                border: "1px solid #ccc",
                padding: "12px",
                marginBottom: "12px",
                borderRadius: "8px",
                backgroundColor: "#fff",
                ...rowStyle,
              }}
            >
              {/* Top Row */}
              <div style={{ 
                display: "flex", 
                justifyContent: "space-between", 
                alignItems: "flex-start",
                marginBottom: "8px"
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  {renderMultipleColumns(topLeftColumns, false)}
                </div>
                <div style={{ flex: 1, textAlign: "right", minWidth: 0 }}>
                  {renderMultipleColumns(topRightColumns, true)}
                </div>
              </div>
              
              {/* Bottom Row */}
              <div style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginTop: "8px"
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  {renderMultipleColumns(bottomLeftColumns, false)}
                </div>
                <div style={{ 
                  flex: 1, 
                  textAlign: "right", 
                  minWidth: 0,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-end"
                }}>
                  {renderMultipleColumns(bottomRightColumns, true)}
                </div>
              </div>
            </div>
          );
        })}
      </Fragment>
    );
  }

  // For desktop view with custom row styling, we'll use a custom table
  if (getRowStyle) {
    return (
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#f6f6f7', borderBottom: '1px solid #e1e3e5' }}>
              {headings.map((heading, index) => (
                <th
                  key={index}
                  style={{
                    padding: '12px 16px',
                    textAlign: columnContentTypes[index] === 'numeric' ? 'right' : 'left',
                    fontWeight: '600',
                    fontSize: '14px',
                    color: '#202223',
                  }}
                >
                  {heading}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {collection.map((item: any, rowIndex: number) => {
              const rowStyle = getRowStyle(item, rowIndex);
              return (
                <tr
                  key={rowIndex}
                  style={{
                    borderBottom: '1px solid #e1e3e5',
                    ...rowStyle,
                  }}
                >
                  {columns.map((column: Column, columnIndex: number) => (
                    <td
                      key={columnIndex}
                      style={{
                        padding: '12px 16px',
                        textAlign: columnContentTypes[columnIndex] === 'numeric' ? 'right' : 'left',
                        fontSize: '14px',
                        color: '#202223',
                      }}
                    >
                      {renderColumnValue(column, item)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <ShopifyDataTable
      columnContentTypes={columnContentTypes}
      headings={headings}
      rows={tableData}
      totalsName={{
        singular: "",
        plural: "",
      }}
      totals={totalsFormatted}
    />
  );
};

export default DataTable;
